import * as React from "react";
import { useState, useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    CheckboxVisibility,
    DetailsList,
    DetailsListLayoutMode,
    IColumn,
    PrimaryButton,
    Checkbox,
    IconButton,
    TextField
} from "@fluentui/react";
import CustomDatePicker from "../../base/CustomDatePicker";
import { RootState } from "../../../state";
import { getEurocodeExceptions } from "../../../state/eurocodeExceptions";
import {
    loadEurocodeExceptions,
    saveEurocodeException,
    removeEurocodeException,
    updateEurocodeExceptionLocal,
    addEurocodeExceptionOptimistic,
    deleteEurocodeExceptionOptimistic
} from "../../../state/eurocodeExceptions/action";
import "./style.css";

let nextTempId = 1;

function formatDate(iso: string): string {
    const d = new Date(iso);
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    return `${dd}-${mm}-${yyyy}`;
}

const EurocodeExceptions: React.FunctionComponent = () => {
    const dispatch = useDispatch();
    const exceptions = useSelector((state: RootState) => getEurocodeExceptions(state.eurocodeExceptions));

    const [editingId, setEditingId] = useState<string | null>(null);
    const [editValues, setEditValues] = useState<Partial<EurocodeException>>({});

    const applyExclusiveBookingFlagToEdit = useCallback((field: "bookingsAllowedAfterRegistration" | "bookingsAllowedUntilRegistrationDate" | "bookingsRejected", checked: boolean) => {
        setEditValues(v => {
            const updated: Partial<EurocodeException> = { ...v, [field]: checked };
            if (!checked) {
                return updated;
            }

            updated.bookingsAllowedAfterRegistration = field === "bookingsAllowedAfterRegistration";
            updated.bookingsAllowedUntilRegistrationDate = field === "bookingsAllowedUntilRegistrationDate";
            updated.bookingsRejected = field === "bookingsRejected";

            return updated;
        });
    }, []);

    const applyExclusiveBookingFlagToRow = useCallback((
        row: EurocodeException,
        field: "bookingsAllowedAfterRegistration" | "bookingsAllowedUntilRegistrationDate" | "bookingsRejected",
        checked: boolean
    ): EurocodeException => {
        if (!checked) {
            return { ...row, [field]: false };
        }

        return {
            ...row,
            bookingsAllowedAfterRegistration: field === "bookingsAllowedAfterRegistration",
            bookingsAllowedUntilRegistrationDate: field === "bookingsAllowedUntilRegistrationDate",
            bookingsRejected: field === "bookingsRejected"
        };
    }, []);

    useEffect(() => {
        dispatch(loadEurocodeExceptions() as any);
    }, []);

    const startEdit = useCallback((row: EurocodeException) => {
        setEditingId(row.id);
        setEditValues({
            euroCode: row.euroCode,
            registrationDate: row.registrationDate,
            bookingsAllowedAfterRegistration: row.bookingsAllowedAfterRegistration,
            bookingsRejected: row.bookingsRejected,
            bookingsAllowedUntilRegistrationDate: row.bookingsAllowedUntilRegistrationDate,
            comment: row.comment,
            customerMessage: row.customerMessage
        });
    }, []);

    const cancelEdit = useCallback(() => {
        if (editingId && editingId.startsWith("new_")) {
            dispatch(deleteEurocodeExceptionOptimistic(editingId));
        }
        setEditingId(null);
        setEditValues({});
    }, [editingId, dispatch]);

    const confirmEdit = useCallback(() => {
        const existing = exceptions.find(e => e.id === editingId);
        if (!existing) return;
        const updated: EurocodeException = {
            ...existing,
            euroCode: editValues.euroCode !== undefined ? editValues.euroCode : existing.euroCode,
            registrationDate: editValues.registrationDate || existing.registrationDate,
            bookingsAllowedAfterRegistration:
                editValues.bookingsAllowedAfterRegistration !== undefined
                    ? editValues.bookingsAllowedAfterRegistration
                    : existing.bookingsAllowedAfterRegistration,
            bookingsRejected:
                editValues.bookingsRejected !== undefined
                    ? editValues.bookingsRejected
                    : existing.bookingsRejected,
            bookingsAllowedUntilRegistrationDate:
                editValues.bookingsAllowedUntilRegistrationDate !== undefined
                    ? editValues.bookingsAllowedUntilRegistrationDate
                    : existing.bookingsAllowedUntilRegistrationDate,
            comment: editValues.comment !== undefined ? editValues.comment : existing.comment,
            customerMessage:
                editValues.customerMessage !== undefined
                    ? editValues.customerMessage
                    : existing.customerMessage
        };
        dispatch(saveEurocodeException(updated) as any);
        setEditingId(null);
        setEditValues({});
    }, [editingId, editValues, exceptions, dispatch]);

    const addNew = useCallback(() => {
        const tempId = `new_${nextTempId++}`;
        const newRow: EurocodeException = {
            id: tempId,
            euroCode: "",
            registrationDate: new Date().toISOString(),
            bookingsAllowedAfterRegistration: false,
            bookingsRejected: false,
            bookingsAllowedUntilRegistrationDate: false,
            comment: "",
            customerMessage: ""
        };
        dispatch(addEurocodeExceptionOptimistic(newRow));
        setEditingId(tempId);
        setEditValues({
            euroCode: "",
            registrationDate: new Date().toISOString(),
            bookingsAllowedAfterRegistration: false,
            bookingsRejected: false,
            bookingsAllowedUntilRegistrationDate: false,
            comment: "",
            customerMessage: ""
        });
    }, [dispatch]);

    const deleteRow = useCallback((id: string) => {
        dispatch(removeEurocodeException(id) as any);
    }, [dispatch]);

    const columns: IColumn[] = [
        {
            key: "index",
            name: "#",
            fieldName: "index",
            minWidth: 30,
            maxWidth: 50,
            onRender: (_item: EurocodeException, index?: number) => <span>{(index !== undefined ? index + 1 : "")}</span>
        },
        {
            key: "euroCode",
            name: "Eurocode",
            fieldName: "euroCode",
            minWidth: 130,
            maxWidth: 180,
            onRender: (row: EurocodeException) =>
                editingId === row.id ? (
                    <TextField
                        value={editValues.euroCode || ""}
                        onChange={(_e, val) => setEditValues(v => ({ ...v, euroCode: val || "" }))}
                        autoFocus={true}
                        borderless={false}
                    />
                ) : (
                    <span className="eurocode__cell-text" onClick={() => startEdit(row)}>
                       {row.euroCode}
                    </span>
                )
        },
        {
            key: "comment",
            name: "Kommentar",
            fieldName: "comment",
            minWidth: 140,
            maxWidth: 220,
            onRender: (row: EurocodeException) =>
                editingId === row.id ? (
                    <TextField
                        value={editValues.comment || ""}
                        onChange={(_e, val) => setEditValues(v => ({ ...v, comment: val || "" }))}
                        borderless={false}
                    />
                ) : (
                    <span className="eurocode__cell-text" onClick={() => startEdit(row)}>
                        {row.comment}
                    </span>
                )
        },
        {
            key: "customerMessage",
            name: "Besked til kunde",
            fieldName: "customerMessage",
            minWidth: 160,
            maxWidth: 240,
            onRender: (row: EurocodeException) =>
                editingId === row.id ? (
                    <TextField
                        value={editValues.customerMessage || ""}
                        onChange={(_e, val) => setEditValues(v => ({ ...v, customerMessage: val || "" }))}
                        borderless={false}
                    />
                ) : (
                    <span className="eurocode__cell-text" onClick={() => startEdit(row)}>
                        {row.customerMessage}
                    </span>
                )
        },
        {
            key: "registrationDate",
            name: "Registreringsdato",
            fieldName: "registrationDate",
            minWidth: 130,
            maxWidth: 160,
            onRender: (row: EurocodeException) =>
                editingId === row.id ? (
                    <CustomDatePicker
                        short={true}
                        date={new Date(editValues.registrationDate || row.registrationDate)}
                        onDateChange={date =>
                            setEditValues(v => ({
                                ...v,
                                registrationDate: date ? date.toISOString() : v.registrationDate
                            }))
                        }
                    />
                ) : (
                    <span className="eurocode__cell-text" onClick={() => startEdit(row)}>
                        {formatDate(row.registrationDate)}
                    </span>
                )
        },
        {
            key: "bookingsAllowedAfterRegistration",
            name: "Efter reg.dato",
            fieldName: "bookingsAllowedAfterRegistration",
            minWidth: 120,
            maxWidth: 120,
            onRender: (row: EurocodeException) => (
                <div className="eurocode__checkbox-cell">
                    <Checkbox
                        checked={
                            editingId === row.id
                                ? editValues.bookingsAllowedAfterRegistration
                                : row.bookingsAllowedAfterRegistration
                        }
                        onChange={(_e, checked) => {
                            if (editingId === row.id) {
                                applyExclusiveBookingFlagToEdit("bookingsAllowedAfterRegistration", !!checked);
                            } else {
                                dispatch(saveEurocodeException(applyExclusiveBookingFlagToRow(row, "bookingsAllowedAfterRegistration", !!checked)) as any);
                            }
                        }}
                    />
                </div>
            )
        },
        {
            key: "bookingsAllowedUntilRegistrationDate",
            name: "Indtil reg.dato",
            fieldName: "bookingsAllowedUntilRegistrationDate",
            minWidth: 120,
            maxWidth: 120,
            onRender: (row: EurocodeException) => (
                <div className="eurocode__checkbox-cell">
                    <Checkbox
                        checked={
                            editingId === row.id
                                ? !!editValues.bookingsAllowedUntilRegistrationDate
                                : row.bookingsAllowedUntilRegistrationDate
                        }
                        onChange={(_e, checked) => {
                            if (editingId === row.id) {
                                applyExclusiveBookingFlagToEdit("bookingsAllowedUntilRegistrationDate", !!checked);
                            } else {
                                dispatch(saveEurocodeException(applyExclusiveBookingFlagToRow(row, "bookingsAllowedUntilRegistrationDate", !!checked)) as any);
                            }
                        }}
                    />
                </div>
            )
        },
        {
            key: "bookingsRejected",
            name: "Afvis",
            fieldName: "bookingsRejected",
            minWidth: 80,
            maxWidth: 80,
            onRender: (row: EurocodeException) => (
                <div className="eurocode__checkbox-cell">
                    <Checkbox
                        checked={editingId === row.id ? !!editValues.bookingsRejected : row.bookingsRejected}
                        onChange={(_e, checked) => {
                            if (editingId === row.id) {
                                applyExclusiveBookingFlagToEdit("bookingsRejected", !!checked);
                            } else {
                                dispatch(saveEurocodeException(applyExclusiveBookingFlagToRow(row, "bookingsRejected", !!checked)) as any);
                            }
                        }}
                    />
                </div>
            )
        },
        {
            key: "actions",
            name: "",
            fieldName: "actions",
            minWidth: 80,
            maxWidth: 80,
            onRender: (row: EurocodeException) =>
                editingId === row.id ? (
                    <div className="eurocode__actions-cell">
                        <IconButton iconProps={{ iconName: "CheckMark" }} onClick={confirmEdit} />
                        <IconButton iconProps={{ iconName: "Cancel" }} onClick={cancelEdit} />
                    </div>
                ) : (
                    <IconButton
                        iconProps={{ iconName: "Delete" }}
                        onClick={() => deleteRow(row.id)}
                    />
                )
        }
    ];

    return (
        <div className="eurocode__container">
            <div className="eurocode__header">
                <h2>Undtagelser</h2>
                <PrimaryButton onClick={addNew} iconProps={{ iconName: "Add" }}>
                    Opret ny
                </PrimaryButton>
            </div>
            <DetailsList
                items={exceptions}
                columns={columns}
                layoutMode={DetailsListLayoutMode.justified}
                compact={true}
                checkboxVisibility={CheckboxVisibility.hidden}
                getKey={(item: EurocodeException) => item.id}
            />
        </div>
    );
};

export default EurocodeExceptions;
